import React, { FC } from 'react'
import { Add, Delete } from '@mui/icons-material'
import { Controller, useFormContext } from 'react-hook-form'
import Loader from '../../../common/Loader'
import { Button, IconButton, Select } from '../../../ui'
import { CurrentPermissionItem } from './PermissionsContent'

interface PermissionsTableProps {
	isLoading: boolean
	permissions: CurrentPermissionItem[]
	onCreateBlank: () => void
	isEditMode: boolean
}

const PermissionsTable: FC<PermissionsTableProps> = ({
	isLoading,
	permissions,
	onCreateBlank,
	isEditMode,
}) => {
	const { control, setValue } = useFormContext()

	return (
		<div className="flex flex-col items-start justify-between text-text-table dark:text-table-dark">
			{permissions?.length ? (
				<div className="max-h-60 overflow-y-auto overflow-x-hidden w-full flex flex-col items-start justify-between text-text-table dark:text-text-table-dark box-border">
					{permissions?.map((item, index) => {
						const currentIndex = permissions?.findIndex(
							({ id }) => id === item?.id
						)
						const type =
							Number(item?.type) === 1
								? 'Group'
								: Number(item?.type) === 2
								? 'Role'
								: 'User'
						const level =
							Number(item?.level) === 1
								? 'Write'
								: Number(item?.level) === 2
								? 'Delete'
								: Number(item?.level) === 3
								? 'None'
								: 'Read'

						return (
							<div
								key={item?.id || index}
								className="h-12 w-full flex items-center border-b last:border-b-0 border-stroke-light dark:border-stroke-light-dark  px-1 box-border"
							>
								<p className="ellipsis w-1/3 md:w-36 border-box text-sm pl-4">
									{type}
								</p>

								<p className="ellipsis w-1/4 md:w-36 mr-2 text-sm pl-4">
									{item?.name}
								</p>

								<div className="w-1/3 md:w-36 border-box py-1.5 px-1">
									{isEditMode ? (
										<Controller
											name={`${
												isEditMode ? 'editingPermissions' : 'permissions'
											}.${currentIndex}.level`}
											control={control}
											render={({ field: { value, onChange } }) => (
												<Select
													className="h-9 w-full"
													value={value ?? 0}
													onChange={(e) => onChange(Number(e))}
													options={[
														{ value: 0, label: 'Read' },
														{ value: 1, label: 'Write' },
														{ value: 2, label: 'Delete' },
														{ value: 3, label: 'None' },
													]}
												/>
											)}
										/>
									) : (
										<p className="ellipsis w-20 md:w-36 mr-2 text-sm pl-4">
											{level}
										</p>
									)}
								</div>

								{isEditMode && (
									<div className="w-11 border-box flex items-center justify-center ml-auto">
										<IconButton
											icon={
												<Delete className="w-5 dark:fill-text-table-dark" />
											}
											variant="secondary"
											onClick={() =>
												setValue(
													isEditMode ? 'editingPermissions' : 'permissions',
													permissions?.filter((p) => p?.id !== item?.id)
												)
											}
											tooltip="Delete permission"
											className="h-9 w-9"
										/>
									</div>
								)}
							</div>
						)
					})}
				</div>
			) : (
				!isEditMode && (
					<div className="flex w-full items-center justify-center text-text-secondary h-44">
						{isLoading ? (
							<Loader size={20} />
						) : (
							<div className="flex w-full mx-auto items-center justify-center gap-2 flex-col">
								<p className="text-sm text-text-table dark:text-text-table-dark">
									Add your first permission
								</p>
								<Button
									variant="secondary"
									leftIcon={<Add />}
									label={null}
									className="w-44 justify-center items-center h-8"
									onClick={onCreateBlank}
								/>
							</div>
						)}
					</div>
				)
			)}
		</div>
	)
}

export default PermissionsTable
